import { useEffect, useState } from 'react';
import type { ChekiOrder, ChekiOrderStatus } from '@tsuyosa/types';
import { useAuth } from '../context/AuthContext';
import { getAdminChekiOrders, updateChekiOrderStatus } from '../lib/api';
import { Spinner } from '@tsuyosa/ui';

const STATUS_OPTIONS: ChekiOrderStatus[] = ['pending', 'paid', 'done', 'cancelled'];

const STATUS_STYLES: Record<ChekiOrderStatus, string> = {
  pending:   'bg-yellow-500/15 text-yellow-400',
  paid:      'bg-blue-500/15 text-blue-400',
  done:      'bg-green-500/15 text-green-400',
  cancelled: 'bg-gray-500/15 text-gray-400',
};

function formatIDR(n: number) {
  return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(n);
}

function formatDate(ts: string) {
  return new Date(ts).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function ChekiOrdersPage() {
  const { accessToken } = useAuth();

  const [orders, setOrders]         = useState<ChekiOrder[]>([]);
  const [isLoading, setIsLoading]   = useState(true);
  const [filterStatus, setFilter]   = useState('');
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [error, setError]           = useState('');

  useEffect(() => {
    if (!accessToken) return;
    setIsLoading(true);
    getAdminChekiOrders(accessToken, filterStatus ? { status: filterStatus } : undefined)
      .then(setOrders)
      .catch(() => setError('Gagal memuat orders.'))
      .finally(() => setIsLoading(false));
  }, [accessToken, filterStatus]);

  async function handleStatusChange(orderId: string, status: string) {
    if (!accessToken) return;
    setUpdatingId(orderId);
    try {
      const updated = await updateChekiOrderStatus(accessToken, orderId, status);
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: updated.status } : o));
    } catch {
      setError('Gagal mengubah status.');
    } finally {
      setUpdatingId(null);
    }
  }

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl font-semibold text-gray-100">Cheki Pre-Orders</h1>
        <select
          value={filterStatus}
          onChange={e => setFilter(e.target.value)}
          className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 text-sm text-gray-200 focus:border-pink-500/50 focus:outline-none"
        >
          <option value="">Semua Status</option>
          {STATUS_OPTIONS.map(s => (
            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
          ))}
        </select>
      </div>

      {error && (
        <p className="mb-4 rounded-lg bg-red-500/10 px-3 py-2 text-xs text-red-400">{error}</p>
      )}

      {isLoading ? (
        <div className="flex justify-center py-20"><Spinner /></div>
      ) : orders.length === 0 ? (
        <div className="py-20 text-center text-sm text-gray-500">Belum ada pre-order cheki.</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-[var(--color-border)]">
          <table className="min-w-full text-sm">
            <thead className="border-b border-[var(--color-border)] bg-black/20">
              <tr>
                {['Pembeli', 'Email', 'Tipe', 'Member', 'Qty', 'Total', 'Status', 'Tanggal', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-400">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--color-border)]">
              {orders.map(order => (
                <tr key={order.id} className="hover:bg-white/2">
                  <td className="px-4 py-3 text-gray-200">{order.buyer_name}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{order.email}</td>
                  <td className="px-4 py-3 text-gray-300 text-xs">
                    {order.type === '2shot_solo' ? '2 Shot Solo' : 'All Member'}
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{order.member?.nickname ?? '—'}</td>
                  <td className="px-4 py-3 text-gray-300 text-center">{order.quantity}</td>
                  <td className="px-4 py-3 text-gray-300 text-xs whitespace-nowrap">{formatIDR(order.total_price)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-medium ${STATUS_STYLES[order.status]}`}>
                      {order.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{formatDate(order.created_at)}</td>
                  <td className="px-4 py-3">
                    <select
                      value={order.status}
                      disabled={updatingId === order.id}
                      onChange={e => handleStatusChange(order.id, e.target.value)}
                      className="rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1 text-xs text-gray-200 focus:border-pink-500/50 focus:outline-none disabled:opacity-50"
                    >
                      {STATUS_OPTIONS.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
